import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { Tooltip } from "@mui/material";
import { IoSearch } from "react-icons/io5";
import NotFound from "../../components/not-found/NotFound";
import { SecondaryButton } from "../../components/Button/Button";
import Switch from "../../components/Switch/Switch";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import findSerialNumber from "../../utils/findSerialNumber";
import { CapitalizeFirstWord } from "../../utils/CapitalizeFirstWord";
import { toastMessage } from "../../utils/toastMessage";
import common from "../../routes/common";
import { consultant, user as userRoutes } from "../../routes/route";
import {
  fetchPowerReturnListApi,
  savePowerReturnSettingsApi,
  updatePowerReturnReminderApi,
} from "../../services/powerCustomerReturn";

const { NOT_FOUND } = common;
const { USER_DETAIL } = userRoutes;

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatDate = (value) => {
  if (!value) return "---";
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00+05:30` : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PowerCustomerReturn = () => {
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search || "", 300);
  const [sendFilter, setSendFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [today, setToday] = useState("");
  const [settings, setSettings] = useState({
    extraDays: 2,
    campaignName: "",
    sendEnabled: false,
  });

  const loadList = useCallback(async () => {
    showLoader();
    try {
      const { data } = await fetchPowerReturnListApi({
        search: debounceValue,
        sendFilter,
        page,
        limit,
      });
      if (data.code === 200) {
        setList(data.result?.list || []);
        setTotal(data.result?.total || 0);
        setToday(data.result?.today || "");
        if (data.result?.settings) {
          setSettings((prev) => ({ ...prev, ...data.result.settings }));
        }
      } else toastMessage(data.message, "error", "powerReturn");
    } catch (error) {
      toastMessage(error.message || "Failed to load return reminders", "error", "powerReturn");
    } finally {
      hideLoader();
    }
  }, [debounceValue, hideLoader, limit, page, sendFilter, showLoader]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const saveSettings = async (nextSettings) => {
    const { data } = await savePowerReturnSettingsApi(nextSettings);
    if (data.code === 200) {
      setSettings(data.result);
      toastMessage("Settings saved", "success", "powerReturn");
      loadList();
    } else toastMessage(data.message, "error", "powerReturn");
  };

  const updateRow = async (row, patch) => {
    const { data } = await updatePowerReturnReminderApi({
      userId: row.userId,
      consultantId: row.consultantId,
      ...patch,
    });
    if (data.code === 200) {
      toastMessage("Updated", "success", "powerReturn");
      loadList();
    } else toastMessage(data.message, "error", "powerReturn");
  };

  return (
    <>
      <div className="WrapperArea">
        <div className="WrapperBox">
          {loader}
          <div className="TitleBox" style={{ alignItems: "flex-start" }}>
            <div>
              <h4 className="Title" style={{ fontWeight: 500, fontSize: 20 }}>
                Power Customer Return Reminders
              </h4>
              <p style={{ margin: "6px 0 0", color: "#666", fontSize: 13, maxWidth: 760 }}>
                Next WhatsApp date = last consult + average return days + common extra days.
                Extra days apply to every customer. Turn send on for a row, keep Auto send on, and the campaign goes on that date.
              </p>
            </div>
          </div>

          <div className="FilterBox FilterArea" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="form-group" style={{ minWidth: 150 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Extra days
              </label>
              <input
                type="number"
                min={0}
                className="form-control"
                value={settings.extraDays}
                onChange={(event) => setSettings((prev) => ({ ...prev, extraDays: event.target.value }))}
              />
            </div>
            <div className="form-group" style={{ minWidth: 240, flex: 1 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                AiSensy campaign name
              </label>
              <input
                type="text"
                className="form-control"
                value={settings.campaignName}
                onChange={(event) => setSettings((prev) => ({ ...prev, campaignName: event.target.value }))}
                placeholder="e.g. power_return_reminder"
              />
            </div>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Auto send
              </label>
              <Switch
                checked={settings.sendEnabled === true}
                onChange={(_event, checked) => setSettings((prev) => ({ ...prev, sendEnabled: Boolean(checked) }))}
              />
            </div>
            <div className="form-group refresh_button" style={{ minWidth: 120 }}>
              <SecondaryButton onClick={() => saveSettings(settings)}>Save settings</SecondaryButton>
            </div>
          </div>

          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className={`form-control ${search && "active"}`}
                placeholder="Search customer, phone, consultant"
                value={search}
                style={{ "--color": "#FF8989" }}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
              <span className="user_search_icon">
                <IoSearch />
              </span>
            </div>
            <div className="form-group select_sm_width" style={{ minWidth: 160 }}>
              <select
                className="form-control"
                value={sendFilter}
                onChange={(event) => {
                  setSendFilter(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">All send status</option>
                <option value="on">Send on</option>
                <option value="off">Send off</option>
                <option value="due">Due today</option>
              </select>
            </div>
            <div className="form-group refresh_button">
              <SecondaryButton onClick={() => { setSearch(""); setSendFilter(""); setPage(1); }}>
                <RotateLeftIcon />
              </SecondaryButton>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Consultant</th>
                  <th>
                    <Tooltip title="Completed private consults with this consultant">
                      <span>Consults</span>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Average gap in days between consults with this consultant">
                      <span>Avg return days</span>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Average successful recharge amount for this customer">
                      <span>Avg recharge</span>
                    </Tooltip>
                  </th>
                  <th>Last consult</th>
                  <th>
                    <Tooltip title={`Last consult + avg return days + ${settings.extraDays || 0} extra days`}>
                      <span>Next WhatsApp date</span>
                    </Tooltip>
                  </th>
                  <th>Send</th>
                </tr>
              </thead>
              <tbody>
                {list.length ? list.map((row, index) => {
                  const key = `${row.userId}:${row.consultantId}`;
                  return (
                    <tr key={key} style={row.dueToday ? { background: "#fff7ed" } : undefined}>
                      <td>{findSerialNumber(index, page, limit)}</td>
                      <td>
                        <Link to={`${USER_DETAIL}/${row.userId}`}>
                          {row.userName ? CapitalizeFirstWord(row.userName) : "---"}
                        </Link>
                        <div style={{ fontSize: 12, color: "#888" }}>{row.customerId || ""}</div>
                      </td>
                      <td>
                        {row.phone ? (
                          <a href={`tel:${row.phone}`} style={{ color: "#FF8989", textDecoration: "none" }}>
                            {row.phonePrefix ? `+${row.phonePrefix}` : ""}{row.phone}
                          </a>
                        ) : "---"}
                      </td>
                      <td>
                        <Link to={`${consultant.CONSULTANT_DETAIL}/${row.consultantId}`}>
                          {row.consultantName ? CapitalizeFirstWord(row.consultantName) : "---"}
                        </Link>
                      </td>
                      <td>{row.consultCount}</td>
                      <td>{row.avgReturnDays ?? "---"}</td>
                      <td>{formatMoney(row.rechargeAverage)}</td>
                      <td>{formatDate(row.lastConsultAt)}</td>
                      <td>
                        {formatDate(row.nextWhatsappDate)}
                        {row.dueToday ? (
                          <div style={{ fontSize: 11, color: "#c2410c", fontWeight: 600 }}>Due today</div>
                        ) : null}
                      </td>
                      <td>
                        <Switch
                          checked={row.sendEnabled === true}
                          onChange={(_event, checked) => updateRow(row, { sendEnabled: Boolean(checked) })}
                        />
                      </td>
                    </tr>
                  );
                }) : (
                  <NotFound cols={10} msg="No power customer return records found" icon={NOT_FOUND} loader={null} />
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <span>Show:</span>
              <select
                value={limit}
                onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}
                style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, minWidth: 80 }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>per page</span>
            </div>
            <span>
              Showing {total ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total || 0)} of {total || 0}
              {today ? ` · Today ${today}` : ""}
            </span>
            {total > limit ? (
              <div className="Pagination">
                <Pagination
                  activePage={page}
                  itemsCountPerPage={limit}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={setPage}
                  itemClass="page-item"
                  linkClass="page-link"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default PowerCustomerReturn;
